// Web-side registry of the workflow action *kinds* (the demodulized `actionType`).
//
// The Phase 0 engine has no display metadata on actions, so the FE owns the
// label / description / completion mapping. All 8 actions are manual placeholders
// in Phase 0 — a person completes each (even the ones that become AI/integrations
// later). The `outputs` here mirror each action's declared `produces` in the seed
// (app/seeds/seed_data/workflows.rb); the engine treats those values as opaque
// (design §8/§11). When the engine later exposes this contract dynamically, this
// file is the single thing to drop.
export interface ActionOption {
  label: string;
  tone?: 'primary' | 'danger';
  // Exactly the `outputs` body to PATCH when this option is chosen.
  outputs: Record<string, string>;
}

export interface ActionKindMeta {
  label: string;
  // Informational only — the API returns the authoritative `categoryKey` per action.
  category: string;
  description: string;
  // Shown above the options when the action needs a person.
  prompt?: string;
  // The manual completion choices; omitted kinds render status-only.
  options?: ActionOption[];
  // The declared value whose stored content identifies the chosen option, so a
  // completed card can show the recorded result.
  resultValueName?: string;
  // Value-mediated dependencies (mirrors the seed's required/produces). The engine
  // doesn't expose these, so the FE keeps them to explain *why* a step is waiting.
  requires: string[];
  produces: string[];
}

// The shared demographic identity, produced by ProcessPacket or entered via pills.
export const IDENTITY_VALUES = ['first_name', 'last_name', 'date_of_birth'];

export const ACTION_KINDS: Record<string, ActionKindMeta> = {
  UploadPacket: {
    label: 'Packet Retrieval and Upload',
    category: 'referral_packet',
    description: 'Retrieve the packet from the source portal and attach it (no real upload in Phase 0).',
    prompt: 'Retrieve the referral packet from the source portal, then mark it uploaded.',
    resultValueName: 'packet_uploaded',
    requires: [],
    produces: ['packet_uploaded'],
    options: [{ label: 'Mark packet uploaded', outputs: { packet_uploaded: 'uploaded' } }],
  },
  ProcessPacket: {
    label: 'Process Referral Packet',
    category: 'referral_packet',
    description: "Extract the patient's demographics from the packet.",
    prompt: 'Confirm the demographics have been captured in Patient details above, then mark this processed.',
    requires: ['packet_uploaded'],
    produces: IDENTITY_VALUES,
    // Produces the demographics, which in Phase 0 are entered via the pills above —
    // so completion just marks the step done (no fabricated values).
    options: [{ label: 'Mark processed', outputs: {} }],
  },
  DuplicatePatientSearch: {
    label: 'Duplicate Patient Search',
    category: 'auto_checks',
    description: "Confirm this patient doesn't already exist, to prevent duplicate records.",
    prompt: 'Record the duplicate-patient result.',
    resultValueName: 'duplicate_patient_result',
    requires: IDENTITY_VALUES,
    produces: ['duplicate_patient_result'],
    options: [
      { label: 'No duplicate found', outputs: { duplicate_patient_result: 'no_match' } },
      { label: 'Possible duplicate', tone: 'danger', outputs: { duplicate_patient_result: 'possible_match' } },
    ],
  },
  SexOffenderCheck: {
    label: 'Sex Offender Check',
    category: 'auto_checks',
    description: 'Check federal/state registries for matches to this patient.',
    prompt: 'Record the registry-check result.',
    resultValueName: 'sex_offender_determination',
    requires: IDENTITY_VALUES,
    produces: ['sex_offender_determination'],
    options: [
      { label: 'No match', outputs: { sex_offender_determination: 'no_match' } },
      { label: 'Possible match', tone: 'danger', outputs: { sex_offender_determination: 'possible_match' } },
    ],
  },
  DenialCheck: {
    label: 'Denial List Check',
    category: 'auto_checks',
    description: "Reference the org's global denial list for matches.",
    prompt: 'Record the denial-list result.',
    resultValueName: 'denial_check_result',
    requires: IDENTITY_VALUES,
    produces: ['denial_check_result'],
    options: [
      { label: 'Not on denial list', outputs: { denial_check_result: 'clear' } },
      { label: 'On denial list', tone: 'danger', outputs: { denial_check_result: 'match' } },
    ],
  },
  BenefitsEligibility: {
    label: 'Insurance Verification',
    category: 'auto_checks',
    description: 'Verify the patient has active insurance coverage.',
    prompt: 'Record the insurance verification result.',
    resultValueName: 'benefits_eligibility_result',
    requires: IDENTITY_VALUES,
    produces: ['benefits_eligibility_result'],
    options: [
      { label: 'Coverage active', outputs: { benefits_eligibility_result: 'active' } },
      { label: 'Coverage inactive', tone: 'danger', outputs: { benefits_eligibility_result: 'inactive' } },
    ],
  },
  ClinicalReview: {
    label: 'Review Clinical Assessment',
    category: 'clinical_review',
    description: "Clinical reviewer's determination.",
    prompt: 'Record your clinical determination.',
    resultValueName: 'clinical_determination',
    requires: IDENTITY_VALUES,
    produces: ['clinical_determination'],
    options: [
      { label: 'Meets criteria', outputs: { clinical_determination: 'meets_criteria' } },
      {
        label: 'Does not meet criteria',
        tone: 'danger',
        outputs: { clinical_determination: 'does_not_meet_criteria' },
      },
    ],
  },
  AcceptReferral: {
    label: 'Referral Determination',
    category: 'decision',
    description: 'The accept/reject decision (writes the outcome).',
    prompt: 'Make the accept or reject decision.',
    resultValueName: 'final_outcome',
    requires: ['clinical_determination'],
    produces: ['final_outcome'],
    options: [
      { label: 'Accept', outputs: { final_outcome: 'accepted' } },
      { label: 'Reject', tone: 'danger', outputs: { final_outcome: 'rejected' } },
    ],
  },
};

export function actionKindMeta(kind: string): ActionKindMeta | undefined {
  return ACTION_KINDS[kind];
}

// The kind that produces a given value name (e.g. `clinical_determination` ->
// `ClinicalReview`), used to explain what a waiting action depends on.
export function producerKindForValue(valueName: string): string | undefined {
  return Object.keys(ACTION_KINDS).find((kind) => ACTION_KINDS[kind].produces.includes(valueName));
}

// A stopgap humanization of a declared (snake_case) value name (`date_of_birth` ->
// `Date of birth`). Swap to the API-provided label once the value-label catalog
// (OLIO-2473) ships — don't grow this into an FE-owned label registry.
export function valueLabel(name: string): string {
  const spaced = name.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}
